import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  fetchSloSilverStagingCurrent,
  fetchSloSilverStagingTrend,
} from '../../api/client';
import {
  MiniLineChart,
  StatusGrid,
  HeatmapGrid,
  ProjectionTable,
  Section,
  MetricsLegend,
  TenantIndicator,
  formatLatency,
  formatLatencyMinutes,
  formatRelativeMinutes,
} from './sloShared';

const POLL_INTERVAL_MS = 30000;

const STAGE2_LEGEND = [
  { term: 'avg', defn: 'Mean of latency_sec — sum of all per-blob latencies divided by count.' },
  { term: 'p50 (median)', defn: 'Half of blobs are at or below this latency. The "typical" experience.' },
  { term: 'p95 (SLO)', defn: '95% of blobs are at or below this. The Bronze→Silver SLO is rolling p95 ≤ 600 s (10 min) over the last 60 minutes.' },
  { term: 'p99', defn: '99% of blobs are at or below this. The "tail" — useful for outlier hunting.' },
  { term: 'max', defn: 'Worst single blob in the window. Spikes here are normal; sustained increases on p95 matter more.' },
];

export default function SilverStagingTab({ active, tenantId = '', tenantLookup = {} }) {
  const [current, setCurrent] = useState(null);
  const [trend, setTrend] = useState(null);
  const [displayedTenantId, setDisplayedTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCurrent = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchSloSilverStagingCurrent({ tenantId });
      setCurrent(data);
      setDisplayedTenantId(tenantId);
      setError('');
    } catch (err) {
      console.error('[SilverStagingTab] current fetch failed', err);
      setError(err?.message || 'Failed to load silver-staging SLO data.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId]);

  const loadTrend = useCallback(async () => {
    try {
      const data = await fetchSloSilverStagingTrend({ days: 30 });
      setTrend(data);
    } catch (err) {
      console.error('[SilverStagingTab] trend fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    loadCurrent(false);
    loadTrend();
    const id = setInterval(() => loadCurrent(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active, loadCurrent, loadTrend]);

  const thresholdSec = current?.thresholdSec ?? trend?.thresholdSec ?? 600;

  const workloadCols = useMemo(() => [
    { key: 'samples', label: 'Samples', align: 'right' },
    { key: 'avgSec', label: 'avg', align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p50Sec', label: 'p50', align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p95Sec', label: 'p95 (SLO)', align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p99Sec', label: 'p99', align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'maxSec', label: 'Max', align: 'right',
      fmt: (v) => formatLatency(v) },
  ], []);

  const heatmapWorkloads = useMemo(() => {
    if (!current?.tenants) return [];
    return Array.from(new Set(current.tenants.map((c) => c.workload))).sort();
  }, [current]);
  const heatmapTenants = useMemo(() => {
    if (!current?.tenants) return [];
    return Array.from(new Set(current.tenants.map((c) => c.tenantId))).sort();
  }, [current]);

  return (
    <div>
      {error && (
        <div className="card mb-4 bg-red-900/20 border-red-700/40 text-red-200 text-sm">
          {error}
        </div>
      )}

      <Section
        title="Current ETL latency (last 1 hour)"
        subtitle={`bronze-staging → silver-staging blob delivery. SLO: rolling p95 ≤ ${thresholdSec}s (10 min).`}
        right={
          <div className="flex items-center gap-3">
            <TenantIndicator
              selectedTenantId={tenantId}
              displayedTenantId={displayedTenantId}
              tenantLookup={tenantLookup}
              loading={loading}
            />
            {current && (
              <div className="text-xs text-gray-500">
                {loading ? 'refreshing…' : `updated ${formatRelativeMinutes(current.generatedUtc)}`}
              </div>
            )}
            <MetricsLegend items={STAGE2_LEGEND} />
          </div>
        }
      >
        <StatusGrid
          rows={current?.workloads || []}
          columns={workloadCols}
          emptyText={loading ? 'Loading…' : 'No silver-staging telemetry in the last hour.'}
        />
      </Section>

      <Section
        title="Per-tenant heatmap"
        subtitle="p95 latency (s) by (workload × tenant), last 1 hour."
      >
        <HeatmapGrid
          cells={current?.tenants || []}
          workloads={heatmapWorkloads}
          tenants={heatmapTenants}
          tenantLookup={tenantLookup}
          valueKey="p95Sec"
          valueLabel="s"
        />
      </Section>

      <Section
        title="30-day p95 trend per workload"
        subtitle={`Daily p95 latency. Dashed line = ${thresholdSec}-second target.`}
      >
        <TrendCharts trend={trend} thresholdSec={thresholdSec} />
      </Section>

      <Section
        title="Days-until-exceeded projection"
        subtitle="Linear regression over the daily p95 series. Most-at-risk workloads first."
      >
        <ProjectionTable
          workloads={trend?.workloads || []}
          targetValue={thresholdSec / 60}
          targetUnit="min"
          currentField="currentP95Sec"
          projectedField="projectedP95In14d"
          valueFormatter={(v) => formatLatencyMinutes(v, 2)}
          slopeFormatter={(v) => v == null ? '—' : (Number(v) / 60).toFixed(3)}
        />
      </Section>
    </div>
  );
}

function TrendCharts({ trend, thresholdSec }) {
  if (!trend || !trend.workloads || trend.workloads.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No trend data yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {trend.workloads.map((w) => {
        const series = (w.dailySeries || []).map((d) => ({ day: d.day, value: d.p95Sec }));
        return (
          <div key={w.workload} className="border border-gray-800 rounded-lg p-3 bg-gray-950/50">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-xs text-gray-300">{w.workload}</span>
              <span className="text-[11px] text-gray-500">
                {w.samples ?? 0} days obs · slope {Number(w.slope ?? 0).toFixed(2)}s/day
              </span>
            </div>
            <MiniLineChart series={series} threshold={thresholdSec} yLabel="s" height={70} />
          </div>
        );
      })}
    </div>
  );
}
